import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, AlertTriangle } from 'lucide-react';
import AuthCard from './AuthCard';
import AuthLayout from './AuthLayout';
import { Alert as ShadcnAlert, AlertDescription, AlertTitle } from '../components/ui/alert';

const useQuery = () => new URLSearchParams(useLocation().search);

export const RegisterForm = () => {
  const navigate = useNavigate();
  const [cookies] = useCookies();
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [loginStatus, setLoginStatus] = useState(0);
  const [passVisible, setPassVisible] = useState(false);
  const [passCnfVisible, setPassCnfVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    const checkLoginStatus = () => {
      if (cookies['token']) {
        window.location.href = '/v2/home';
      }
    };
    if (loginStatus === 0) {
      checkLoginStatus();
    }
    return () => {};
  }, [loginStatus]);

  const validateName = (name) => {
    const nameRegex = /^[a-zA-Z]+((['. ][a-zA-Z ])?[a-zA-Z]*)*$/;
    return nameRegex.test(name) && name.length >= 3;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'name') {
      // Filter out characters that are not letters, spaces, apostrophes, or periods
      newValue = value.replace(/[^a-zA-Z '. ]/g, '');
      setFormData((prev) => ({ ...prev, [name]: newValue }));

      if (newValue.trim() === '') {
        setErrors((prev) => ({ ...prev, name: undefined }));
      } else if (!validateName(newValue)) {
        setErrors((prev) => ({ ...prev, name: 'Name should be minimum 3 characters' }));
      } else {
        setErrors((prev) => ({ ...prev, name: undefined }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: newValue }));
      if (name === 'email') {
        if (newValue.trim() === '') {
          setErrors((prev) => ({ ...prev, email: undefined }));
        } else if (!validateEmail(newValue)) {
          setErrors((prev) => ({ ...prev, email: 'Please enter a valid email' }));
        } else {
          setErrors((prev) => ({ ...prev, email: undefined }));
        }
      } else if (name === 'password') {
        if (newValue.trim() === '') {
          setErrors((prev) => ({ ...prev, password: undefined }));
        } else if (newValue.length < 6) {
          setErrors((prev) => ({ ...prev, password: 'Password must be at least 6 characters' }));
        } else {
          setErrors((prev) => ({ ...prev, password: undefined }));
        }
        if (formData.confirmPassword && newValue !== formData.confirmPassword) {
          setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords must match' }));
        } else if (formData.confirmPassword && newValue === formData.confirmPassword) {
          setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
        }
      } else if (name === 'confirmPassword') {
        if (newValue.trim() === '') {
          setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
        } else if (formData.password !== newValue) {
          setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords must match' }));
        } else {
          setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
        }
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name) {
      newErrors.name = 'This field is required';
    } else if (!validateName(formData.name)) {
      newErrors.name = 'Name should only contain letters and spaces (3-25 characters)';
    }

    if (!formData.email) {
      newErrors.email = 'This field is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'This field is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'This field is required';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords must match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Trim whitespace from all form data fields
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedPassword = formData.password.trim();
    const trimmedConfirmPassword = formData.confirmPassword.trim();

    // Update formData with trimmed values for validation and submission
    setFormData((prev) => ({
      ...prev,
      name: trimmedName,
      email: trimmedEmail,
      password: trimmedPassword,
      confirmPassword: trimmedConfirmPassword,
    }));

    const newErrors = {};

    if (!trimmedName) {
      newErrors.name = 'This field is required';
    } else if (!validateName(trimmedName)) {
      newErrors.name = 'Name should only contain letters and spaces (3-25 characters)';
    }

    if (!trimmedEmail) {
      newErrors.email = 'This field is required';
    } else if (!validateEmail(trimmedEmail)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!trimmedPassword) {
      newErrors.password = 'This field is required';
    } else if (trimmedPassword.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!trimmedConfirmPassword) {
      newErrors.confirmPassword = 'This field is required';
    } else if (trimmedPassword !== trimmedConfirmPassword) {
      newErrors.confirmPassword = 'Passwords must match';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);
    setShowAlert(false);
    setAlertMessage('');

    axios
      .post(`/api/auth/user/manualRegister`, {
        name: trimmedName,
        email: trimmedEmail,
        password: trimmedPassword,
      })
      .then((res) => {
        if (res?.status === 200) {
          setShowDetails(true);
        } else {
          setLoading(false);
          setAlertMessage(
            "Could not create an account. Please check your email and ensure that you haven't created an account already",
          );
          setShowAlert(true);
        }
      })
      .catch((error) => {
        setLoading(false);
        setAlertMessage(
          "Could not create an account. Please check your email and ensure that you haven't created an account already",
        );
        setShowAlert(true);
      });
  };

  const handleBackToLogin = () => {
    navigate('/auth/login');
  };

  if (showDetails) {
    return (
      <AuthLayout>
        <AuthCard
          title="Email Verification"
          subtitle="A verification link has been been sent to your email. Please follow the instructions specified in the email."
          showBackButton={true}
          onBack={handleBackToLogin}
        >
          <div className="text-center mt-8">
            <p className="text-sm" style={{ color: '#60748f' }}>
              Having trouble?
              <br />
              <a
                href="mailto:support@mathongo.com?subject=I cannot login to Quizrr"
                className="underline hover:no-underline"
                style={{ color: '#4338ca' }}
              >
                Contact us
              </a>
            </p>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  const isFormValid =
    formData.name &&
    formData.email &&
    formData.password &&
    formData.confirmPassword &&
    validateName(formData.name) &&
    validateEmail(formData.email) &&
    formData.password.length >= 6 &&
    formData.password === formData.confirmPassword;

  return (
    <AuthLayout>
      <div className="w-full max-w-md">
        <AuthCard
          title="Sign up"
          subtitle="Create a new Quizrr account"
          showBackButton={true}
          onBack={handleBackToLogin}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="name" style={{ color: '#1d2933' }}>
                Name
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                className={`h-[40px] rounded-full ${errors?.name ? 'border-[#f3164e]' : ''}`}
                style={{
                  backgroundColor: '#ffffff',
                  border: errors?.name ? '1px solid #f3164e' : '1px solid #d9e6f2',
                  color: '#1d2933',
                }}
              />
              {errors?.name && (
                <div className="flex items-center gap-1 text-sm" style={{ color: '#f3164e' }}>
                  <AlertTriangle size={16} className="text-[#f3164e]" />
                  {errors.name}
                </div>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: '#1d2933' }}>
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="off"
                placeholder="name@address.com"
                className={`h-[40px] rounded-full ${errors?.email ? 'border-[#f3164e]' : ''}`}
                style={{
                  backgroundColor: '#ffffff',
                  border: errors?.email ? '1px solid #f3164e' : '1px solid #d9e6f2',
                  color: '#1d2933',
                }}
              />
              {errors?.email && (
                <div className="flex items-center gap-1 text-sm" style={{ color: '#f3164e' }}>
                  <AlertTriangle size={16} className="text-[#f3164e]" />
                  {errors.email}
                </div>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: '#1d2933' }}>
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={passVisible ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  className={`h-[40px] rounded-full pr-12 ${errors?.password ? 'border-[#f3164e]' : ''}`}
                  style={{
                    backgroundColor: '#ffffff',
                    border: errors?.password ? '1px solid #f3164e' : '1px solid #d9e6f2',
                    color: '#1d2933',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setPassVisible(!passVisible)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                  style={{ color: '#60748f' }}
                >
                  {passVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors?.password && (
                <div className="flex items-center gap-1 text-sm" style={{ color: '#f3164e' }}>
                  <AlertTriangle size={16} className="text-[#f3164e]" />
                  {errors.password}
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" style={{ color: '#1d2933' }}>
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={passCnfVisible ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Enter your password again"
                  className={`h-[40px] rounded-full pr-12 ${errors?.confirmPassword ? 'border-[#f3164e]' : ''}`}
                  style={{
                    backgroundColor: '#ffffff',
                    border: errors?.confirmPassword ? '1px solid #f3164e' : '1px solid #d9e6f2',
                    color: '#1d2933',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setPassCnfVisible(!passCnfVisible)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                  style={{ color: '#60748f' }}
                >
                  {passCnfVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors?.confirmPassword && (
                <div className="flex items-center gap-1 text-sm" style={{ color: '#f3164e' }}>
                  <AlertTriangle size={16} className="text-[#f3164e]" />
                  {errors.confirmPassword}
                </div>
              )}
            </div>
            {showAlert && (
              <div className="flex items-center justify-center text-sm text-center" style={{ color: '#f3164e' }}>
                <AlertTriangle size={25} className="text-[#f3164e]" />
                Registration Failed : {alertMessage}
              </div>
            )}
            <Button
              type="submit"
              disabled={!isFormValid}
              className={`w-full rounded-full h-[40px] text-base font-medium ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{
                backgroundColor: '#4338ca',
                color: '#ffffff',
                border: 'none',
              }}
            >
              {loading ? 'Signing Up...' : 'Sign Up'}
            </Button>

            <div className="text-center">
              <p className="text-sm" style={{ color: '#60748f' }}>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="underline hover:no-underline"
                  style={{ color: '#4338ca' }}
                >
                  Log in
                </button>
              </p>
            </div>
          </form>
        </AuthCard>
      </div>
    </AuthLayout>
  );
};

export const RegisterVerify = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const [cookies] = useCookies();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginStatus, setLoginStatus] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const token = query.get('token');

  useEffect(() => {
    const checkLoginStatus = () => {
      if (cookies['token']) {
        window.location.href = '/v2/home';
      }
    };
    if (loginStatus === 0) {
      checkLoginStatus();
    }
    return () => {};
  }, [loginStatus]);

  useEffect(() => {
    if (token && token.length > 0) {
      setLoading(true);
      setError(false);
      setShowAlert(false);
      setAlertMessage('');
      axios
        .post(`/api/auth/user/verifyEmail`, { token })
        .then((res) => {
          setLoading(false);
        })
        .catch((err) => {
          setError(true);
          setLoading(false);
          setAlertMessage('Your email could not be verified. Please try again or contact support.');
          setShowAlert(true);
          console.log(err);
        });
    } else {
      setError(true);
      setLoading(false);
      setAlertMessage('Invalid verification link. Please request a new one.');
      setShowAlert(true);
    }
  }, [token]);

  const handleBackToLogin = () => {
    navigate('/auth/login');
  };

  if (loading) {
    return <>Loading...</>;
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-md">
        <AuthCard
          title={!error ? 'Email Verified' : "Email couldn't be verified"}
          subtitle={
            !error
              ? 'Your email has been verified.'
              : 'Your email could not be verified. Please check your email for a new verification link or contact us.'
          }
          showBackButton={true}
          onBack={handleBackToLogin}
        >
          {showAlert && (
            <div className="flex items-center justify-center text-sm text-center" style={{ color: '#f3164e' }}>
              <AlertTriangle size={25} className="text-[#f3164e]" />
              Verification Failed : {alertMessage}
            </div>
          )}
          <div className="space-y-6">
            {!error && (
              <Button
                onClick={handleBackToLogin}
                className="w-full h-[40px] text-base font-medium rounded-full"
                style={{
                  backgroundColor: '#4338ca',
                  color: '#ffffff',
                  border: 'none',
                }}
              >
                Go to Login
              </Button>
            )}

            <div className="text-center">
              <p className="text-sm" style={{ color: '#60748f' }}>
                {!error && (
                  <>
                    Having trouble?
                    <br />
                    <a
                      href="mailto:support@mathongo.com?subject=I cannot login to Quizrr"
                      className="underline hover:no-underline"
                      style={{ color: '#4338ca' }}
                    >
                      Contact us
                    </a>
                  </>
                )}
              </p>
            </div>
          </div>
        </AuthCard>
      </div>
    </AuthLayout>
  );
};
